import { useCallback, useMemo, useRef } from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import SectionNegativePrompt from "domains/inference/components/InferenceGenerator/Sidebar/SectionNegativePrompt";
import SectionPrompt from "domains/inference/components/InferenceGenerator/Sidebar/SectionPrompt";
import { useJobSessionContext } from "domains/jobs/contexts/JobSessionProviderV2";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { LaunchShortcut } from "domains/shortcuts/components/Shortcut";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import ButtonWithCuIndicator from "domains/ui/components/ButtonWithCuIndicator";
import Divider from "domains/ui/components/Divider";
import TourGuideIcon from "domains/ui/components/TourGuideIcon";
import { useWindowSize } from "domains/ui/hooks/useWindowSize";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import { usePostTxt2ImgInferencesMutation } from "infra/api/generated/api";
import { useGetInferenceCostQuery } from "infra/store/apiSlice";
import _ from "lodash";

import { Box, Flex, HStack, VStack } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";

import BlendConceptSliders from "./BlendConceptSlider";

const INFERENCE_DEFAULT_PARAMS = {
  numSamples: 4,
  width: 1_024,
  height: 1_024,
  prompt: "",
  negativePrompt: "",
};

const FM_STYLE_PROPS = {
  header: {
    top: "0px",
    bgColor: "backgroundTertiary.500",
  },
};

export default function BlendTryOut() {
  const [createInferenceTrigger] = usePostTxt2ImgInferencesMutation();
  const { selectedTeam, selectedProject, isReader } = useTeamContext();
  const { showLimitModal } = usePlanContext();
  const { successToast, errorToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { width: windowWidth } = useWindowSize();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const {
    isGenerateLoading,
    setIsGenerateLoading,
    isGenerateDisabled,
    getModel,
    concepts,
    negativePrompt,
    setNegativePrompt,
    prompt,
    setPrompt,
    type,
  } = useCreateBlendContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const isMobile = windowWidth !== undefined && windowWidth < 1_200;
  const numSamples = selectedTeam.isFreePlan ? 2 : 4;

  const placeholders = useGetFileImagePlaceholders({
    length: numSamples,
    dimensions: {
      width: INFERENCE_DEFAULT_PARAMS.width,
      height: INFERENCE_DEFAULT_PARAMS.height,
    },
  });

  const inferenceDefaultParams = useMemo(
    () => ({
      ...INFERENCE_DEFAULT_PARAMS,
      numSamples: selectedTeam.isFreePlan ? 2 : 4,
      guidance: type === "sd-xl" ? 6 : 3.5,
    }),
    [selectedTeam.isFreePlan, type]
  );
  const { data: cuCostData, isFetching: isCuLoading } =
    useGetInferenceCostQuery(
      !isReader
        ? {
            inferenceType: "txt2img",
            projectId: selectedProject.id,
            body: {
              modelId: "stable-diffusion-xl-base-1.0",
              ...inferenceDefaultParams,
            },
          }
        : skipToken
    );

  // ----------------------------------

  const createInference = useCallback(async () => {
    if (isGenerateDisabled) {
      return;
    }
    setIsGenerateLoading(true);
    const model = await getModel();
    if (model) {
      try {
        const data = await createInferenceTrigger({
          projectId: selectedProject.id,
          body: {
            ...inferenceDefaultParams,
            modelId: model.id,
            prompt,
            negativePrompt,
            concepts,
          },
        })
          .unwrap()
          .then(removeCuFromType);
        addJob(data.job);
        Track(AnalyticsEvents.Blend.Tested, {
          modelId: model.id,
          prompt,
          concepts,
        });
        successToast({
          title: "Images are being generated",
        });
      } catch (error: any) {
        handleApiError(error, "Error generating images", {
          quota: () => {
            if (_.get(error, "data.details.remainingSeconds")) {
              showLimitModal("planCooldown", {
                timeout: error.data.details.remainingSeconds,
                type: "generation",
              });
            } else if (_.get(error, "data.name") === "RateLimitError") {
              showLimitModal("planImageGenerationsRateLimit");
            } else if (
              _.get(error, "data.details.actionName") === "parallel-inferences"
            ) {
              showLimitModal("parallelInferences");
            } else if (
              _.get(error, "data.reason").includes(
                "You have reached your plan's limit."
              )
            ) {
              showLimitModal("notEnoughCreativeUnits");
            } else {
              errorToast({
                title: "Error generating images",
                description: _.get(error, "data.reason"),
              });
            }
          },
        });
      }
    }

    setIsGenerateLoading(false);
  }, [
    isGenerateDisabled,
    getModel,
    setIsGenerateLoading,
    prompt,
    negativePrompt,
    createInferenceTrigger,
    selectedProject.id,
    inferenceDefaultParams,
    concepts,
    addJob,
    successToast,
    handleApiError,
    showLimitModal,
    errorToast,
  ]);

  // ----------------------------------

  // Meta + Enter doesn't work on my Windows, so I added Control + Enter
  useHotkeys(
    "Meta+Enter,Control+Enter",
    () => {
      if (document.activeElement) {
        (document.activeElement as HTMLElement).blur();
      }
      void createInference();
    },
    [createInference],
    {
      enableOnFormTags: true,
    }
  );

  // ----------------------------------

  return (
    <VStack pos="relative" flex={1} w="100%" h="100%">
      <HStack
        w="100%"
        h="100%"
        textAlign="start"
        bgColor="backgroundTertiary.500"
      >
        <Flex direction={isMobile ? "column" : "row"} w="100%" h="100%">
          <VStack
            zIndex="calc(var(--chakra-zIndices-sticky) + 1)"
            align="stretch"
            overflow="auto"
            overflowX={"auto"}
            w={isMobile ? "100%" : "375px"}
            h={isMobile ? "unset" : "100%"}
            borderRightWidth={isMobile ? 0 : 1}
            borderBottomWidth={isMobile ? 1 : 0}
            bgColor="secondary.900"
            spacing={0}
          >
            <Flex direction="column">
              <BlendConceptSliders />

              <Divider />
              <SectionPrompt
                id="default"
                prompt={prompt}
                setPrompt={setPrompt}
                getModel={getModel}
              />

              <Divider />
              {type !== "flux.1" && (
                <>
                  <SectionNegativePrompt
                    id="negativePrompt"
                    prompt={negativePrompt}
                    setPrompt={setNegativePrompt}
                  />
                  <Divider />
                </>
              )}

              <SidebarSection>
                <ButtonWithCuIndicator
                  onClick={createInference}
                  isLoading={isGenerateLoading}
                  isDisabled={isGenerateDisabled}
                  variant="primary"
                  w="100%"
                  cuCost={cuCostData?.creativeUnitsCost}
                  isCuLoading={isCuLoading}
                  tooltip={!isGenerateDisabled && <LaunchShortcut />}
                  tooltipProps={{
                    placement: "right",
                    hasArrow: true,
                    isDisabled: isGenerateDisabled,
                  }}
                >
                  Generate
                </ButtonWithCuIndicator>
              </SidebarSection>
              <Divider />
            </Flex>
          </VStack>

          <Flex
            ref={scrollRef}
            direction="column"
            flex={1}
            gap={2}
            overflowX="hidden"
            overflowY="auto"
            px={8}
          >
            <Box mb={4}>
              <FileManagerImage
                scrollRef={scrollRef}
                files={
                  !files?.length && !jobs?.length && !isLoading
                    ? placeholders
                    : files
                }
                jobs={jobs}
                {...fileManagerProps}
                isSelectable
                isLoading={isLoading}
                isCardWithoutActions
                isVaryEnabled={false}
                EmptyStateComponent={EmptyState}
                headerLeftContent={<TourGuideIcon guide="composition-tests" />}
                headerRightContent={!isMobile ? <Box w={6} /> : undefined}
                ignoreActions={["editCollections", "editTags", "removeBg"]}
                styleProps={FM_STYLE_PROPS}
              />
            </Box>
          </Flex>
        </Flex>
      </HStack>
    </VStack>
  );
}

// ------------------------------------

function EmptyState() {
  return <></>;
}
